import moment from 'moment'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { gqlQuery } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logError, logWarn } from 'lib/logging'
import { SCHEDULE_DAY_LOCATION_QUERY, SCHEDULE_OPERATORIES_QUERY, SCHEDULE_ROOM_SCHEDULES_QUERY } from './graphql'
import { slotMinutes } from './scheduleGrid'
import { Day, DayAppointment, RoomSchedule, buildDay, horizonEnd, nextWorkingDate } from './scheduleDay'

/** How far ahead to read the rooms' schedules, for the next-working-day jump. */
const HORIZON_DAYS = 21

type RawSchedule = {
  name?: string
  type?: string
  date: string
  blocks?: { startTime?: string; endTime?: string }[] | null
  notes?: { startTime?: string; endTime?: string }[] | null
}

/** Wall clock pairs into minutes, dropping anything unreadable rather than guessing. */
function toIntervals(spans?: { startTime?: string; endTime?: string }[] | null) {
  return (spans ?? []).flatMap((span) => {
    const start = slotMinutes(span.startTime ?? ``)
    const end = slotMinutes(span.endTime ?? ``)
    return start === undefined || end === undefined || end <= start ? [] : [{ start, end }]
  })
}

/**
 * The practice's own scheduling engine, asked for the whole day at once.
 *
 * `/agent/availabilities` answers per appointment category and flattens every
 * open time to a count, throwing away the room it is in. `scheduling-data`
 * returns the same generator's output unflattened, so each open time still
 * carries the `Availability` records behind it and therefore its `operatoryId`.
 * One call instead of four probes, no category mapping, and the room comes back.
 *
 * Verified against Hamblin on 2026-08-15: 24KB in about two seconds, covering
 * eight days from the day requested, `operatoryId` intact on every record.
 */
/**
 * What the scheduling call is read for now: the rooms, the day's booked work,
 * and the practice's slot length.
 *
 * Its `slots` and `availabilities` are deliberately ignored. Those are
 * Trackable's availability configuration, which describes what the scheduler is
 * set up to offer rather than what the practice has free, and the two are far
 * apart: on Monday 31 August at Serenity the configuration offered one hour
 * while four rooms sat empty for six and a half. The board works the free time
 * out of the rooms' own PMS schedules instead.
 */
type SchedulingData = {
  /**
   * The location's rooms and that day's booked work, restored by TRK-5515.
   *
   * `name` is the practice's own name for the room. The appointments carry wall
   * clock times, the room, and the cancelled and broken flags, which is rules 2,
   * 3 and 4 of the design in one payload. Verified on dev 2026-08-18: 14 rooms,
   * 45 appointments, every `operatoryId` matching a room.
   */
  operatories?: { id?: string; name?: string; resourceName?: string }[]
  appointments?: DayAppointment[]
  /**
   * Whether the PMS answered. `ok` on an empty day, `error` when the practice
   * management system could not be reached, so an outage never draws as a
   * practice with nothing booked.
   */
  pmsStatus?: string
  /**
   * The practice's own slot length, in minutes.
   *
   * The engine reports an opening as a start time and nothing else, so without
   * this a chip can only say "2 PM" and the block is drawn at a guessed height.
   * Hamblin's granularity is 60, so their openings are an hour: drawing them at
   * the old thirty-minute default halved every one of them on screen.
   */
  schedulingConfiguration?: { granularity?: number | null } | null
}

export type ScheduleDay = {
  day: Day
  date: string
  loading: boolean
  /**
   * The engine could not be asked. Never the same as nothing being open: a day
   * drawn empty because a fetch failed would read as a fully booked practice.
   */
  failed: boolean
  /**
   * The practice management system did not answer, so the booked work is
   * missing rather than absent. Drawing empty rooms without saying this would
   * tell a practice their day is free.
   */
  pmsUnavailable: boolean
  /** The rooms' hours query failed, so closed-looking time is unknown, not closed. */
  hoursUnavailable: boolean
  /** How many rooms the PMS lists, against how many can be booked into. */
  roomCount: number
  /**
   * The soonest day after this one with a chair going spare, within the window
   * the engine answered for. A day with nothing open is otherwise a dead end
   * that has to be clicked out of one day at a time.
   */
  nextOpening?: string
  /** The last day the engine answered for, so the board can say how far it looked. */
  horizon?: string
  goToDate: (date: string) => void
  reload: () => void
}

export function useScheduleDay(locationId: string | undefined, initialDate?: string): ScheduleDay {
  const [date, setDate] = useState(() => initialDate || moment().format(`YYYY-MM-DD`))
  const [schedules, setSchedules] = useState<RoomSchedule[]>([])
  const [workingDates, setWorkingDates] = useState<string[]>([])
  const [appointments, setAppointments] = useState<DayAppointment[]>([])
  const [pmsUnavailable, setPmsUnavailable] = useState(false)
  const [hoursUnavailable, setHoursUnavailable] = useState(false)
  const [granularity, setGranularity] = useState<number | undefined>(undefined)
  const [rooms, setRooms] = useState<{ operatoryId: string; label: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [reloads, setReloads] = useState(0)
  const run = useRef(0)

  useEffect(() => {
    if (initialDate) setDate(initialDate)
  }, [initialDate])

  useEffect(() => {
    run.current += 1
    const token = run.current
    // No location to ask about is an answer, not a wait. Returning while
    // `loading` was still true left the board showing "…" and "rooms working of
    // 0" for ever, which is the shape of a page that is about to say something
    // and never does.
    if (!locationId) {
      setLoading(false)
      return
    }
    setLoading(true)
    setFailed(false)
    bg(
      Promise.all([
        window
          .fetch(`/api/scheduling/locations/${encodeURIComponent(locationId)}/scheduling-data?day=${date}`, {
            credentials: `include`,
          })
          .then(async (response) => {
            if (!response.ok) throw new Error(`scheduling-data ${response.status}`)
            return (await response.json()) as SchedulingData
          }),
        gqlQuery<{ operatories?: { remote_id?: string; display_name?: string; name?: string }[] }>(
          SCHEDULE_OPERATORIES_QUERY,
          { locationId },
        ),
        /**
         * Every room's working hours from here to the horizon.
         *
         * One range query rather than one per day: the days beyond this one are
         * what the "next working day" jump is made of, and Serenity has no
         * schedules at all on a Friday, so the answer from a Friday is Monday.
         *
         * Scoped to this practice's sync, or two practices sharing a workspace
         * trade rooms (see `SCHEDULE_DAY_LOCATION_QUERY`). The sync keys records
         * on the location's `kollaConsumerId` and falls back to its id, the same
         * way the PMS Schedules page reads them.
         */
        gqlQuery<{ location?: { kollaConsumerId?: string | null } | null }>(SCHEDULE_DAY_LOCATION_QUERY, {
          id: locationId,
        }).then((locationResult) => {
          const consumerId = locationResult.data?.location?.kollaConsumerId || locationId
          const horizon = moment(date).add(HORIZON_DAYS, `days`).format(`YYYY-MM-DD`)
          return gqlQuery<{ resourceSchedules?: { hits?: RawSchedule[] } }>(SCHEDULE_ROOM_SCHEDULES_QUERY, {
            search: `kollaConsumerId:${consumerId} AND date:[${date} TO ${horizon}]`,
            pagination: { limit: 1000 },
          })
        }),
      ])
        .then(([data, operatoryResult, scheduleResult]) => {
          if (token !== run.current) return
          setAppointments((data.appointments ?? []).filter((a) => a && a.operatoryId != null))
          setPmsUnavailable(data.pmsStatus === `error`)
          setGranularity(data.schedulingConfiguration?.granularity ?? undefined)

          const scheduleHits = scheduleResult.data?.resourceSchedules?.hits ?? []
          const rooms = scheduleHits.filter((hit) => hit.type === `OPERATORY`)
          setWorkingDates([...new Set(rooms.filter((hit) => hit.blocks?.length).map((hit) => hit.date))])
          setSchedules(
            rooms
              .filter((hit) => hit.date === date)
              .map((hit) => ({
                operatoryId: String(hit.name ?? ``).replace(`resources/operatory_`, ``),
                blocks: toIntervals(hit.blocks),
                blocked: toIntervals(hit.notes),
              })),
          )
          // Said, not swallowed: with no blocks every room draws as closed,
          // and a failed hours query rendering as a closed practice is the
          // schedule lying about the one thing it exists to show.
          setHoursUnavailable(!!scheduleResult.error)
          if (scheduleResult.error) logWarn(`Schedule could not read the rooms' hours`, scheduleResult.error.message)

          // The rooms as the scheduling payload carries them, which is the same
          // call the booked work arrives on, so the two cannot disagree about
          // which rooms exist. The synced collection stays as the fallback for
          // an API that has not shipped TRK-5515 yet, and its labels are the
          // same names.
          const fromPayload = (data.operatories ?? []).flatMap((room) =>
            room.id ? [{ operatoryId: String(room.id), label: room.name || `Room ${room.id}` }] : [],
          )
          const fromGraphql = (operatoryResult.data?.operatories ?? []).flatMap((room) =>
            room.remote_id
              ? [
                  {
                    operatoryId: String(room.remote_id),
                    label: room.display_name || room.name || `Room ${room.remote_id}`,
                  },
                ]
              : [],
          )
          setRooms(fromPayload.length ? fromPayload : fromGraphql)
          if (operatoryResult.error) logWarn(`Schedule could not read the rooms`, operatoryResult.error.message)
          setLoading(false)
        })
        .catch((err) => {
          if (token !== run.current) return
          logError(`Schedule could not read the day`, err)
          setSchedules([])
          setFailed(true)
          setLoading(false)
        }),
    )
  }, [locationId, date, reloads])

  const day = useMemo(
    () => buildDay({ date, appointments, operatories: rooms, schedules, minimumGap: granularity }),
    [date, appointments, rooms, schedules, granularity],
  )

  const goToDate = useCallback((next: string) => setDate(next), [])
  const reload = useCallback(() => setReloads((count) => count + 1), [])

  return {
    day,
    date,
    loading,
    failed,
    pmsUnavailable,
    hoursUnavailable,
    roomCount: rooms.length,
    nextOpening: nextWorkingDate(workingDates, date),
    horizon: horizonEnd(workingDates.length ? workingDates : [date]),
    goToDate,
    reload,
  }
}
